/**
 * Hooks React Query de la publication réseaux sociaux du blog (admin).
 *
 * Lectures (`blog_social_posts`) via le client RLS admin ; écritures (OAuth,
 * rendu carrousel, publication) via les edge fns service_role. Réutilise
 * `useSocialAccount(platform)` de `youtube-queries` pour l'état de connexion.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ytDb } from './youtube-queries';
import { blogArticleQueryKey } from './use-blog';
import {
  postBlogSocial,
  renderBlogCarouselAll,
  startSocialOAuth,
  updateSocialConfig,
  type CarouselPlatform,
  type CarouselSlide,
  type SocialAppConfig,
  type SocialConfigPatch,
  type SocialNetwork,
  type SocialProvider,
} from './social-client';

export type BlogSocialPostStatus = 'idle' | 'posting' | 'posted' | 'failed';

export interface BlogSocialPostRow {
  platform: SocialNetwork;
  status: BlogSocialPostStatus;
  external_id: string | null;
  external_url: string | null;
  error: string | null;
  updated_at: string;
}

export const blogSocialPostsQueryKey = (articleId: string) =>
  ['admin', 'blog-social-posts', articleId] as const;

/** Publications (par réseau) d'un article — indexées par plateforme côté UI. */
export function useBlogSocialPosts(articleId: string) {
  return useQuery({
    queryKey: blogSocialPostsQueryKey(articleId),
    enabled: !!articleId,
    queryFn: async (): Promise<BlogSocialPostRow[]> => {
      const { data, error } = await ytDb
        .from('blog_social_posts')
        .select('platform, status, external_id, external_url, error, updated_at')
        .eq('article_id', articleId);
      if (error) throw new Error(error.message);
      return (data ?? []) as BlogSocialPostRow[];
    },
  });
}

export function useStartSocialOAuth() {
  return useMutation({ mutationFn: (provider: SocialProvider) => startSocialOAuth(provider) });
}

/**
 * Rend tout le carrousel d'une plateforme, slide par slide (1 invocation Edge
 * chacun — cf. budget compute). `onProgress` reçoit le set partiel après chaque
 * slide pour un affichage incrémental côté UI.
 */
export function useRenderBlogCarousel(articleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      platform,
      onProgress,
    }: {
      platform: CarouselPlatform;
      onProgress?: (slides: CarouselSlide[], done: number, total: number) => void;
    }) => renderBlogCarouselAll(articleId, platform, onProgress),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: blogArticleQueryKey(articleId) });
    },
  });
}

export function usePostBlogSocial(articleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (platform: SocialNetwork) => postBlogSocial(articleId, platform),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: blogSocialPostsQueryKey(articleId) });
    },
  });
}

// ── Paramètres de connexion (social_app_configs, migration 0132) ─────────────

export const socialConfigsQueryKey = ['admin', 'social-configs'] as const;

/** Configs de connexion (lecture directe, RLS admin) indexées par provider côté UI. */
export function useSocialConfigs() {
  return useQuery({
    queryKey: socialConfigsQueryKey,
    queryFn: async (): Promise<SocialAppConfig[]> => {
      const { data, error } = await ytDb
        .from('social_app_configs')
        .select(
          'provider, client_id, page_id, org_id, api_version, redirect_uri, secret_set, updated_at',
        );
      if (error) throw new Error(error.message);
      return (data ?? []) as SocialAppConfig[];
    },
  });
}

export function useUpdateSocialConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { provider: SocialProvider; patch: SocialConfigPatch }) =>
      updateSocialConfig(args.provider, args.patch),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: socialConfigsQueryKey });
    },
  });
}
